import { QuestionCircleOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Col, Collapse, Descriptions, Row, Tooltip, Typography } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, getPointPermission, MosaicComponent } from '@/components'
import { ResourceUsers } from '@/components/resouce-users/resource-users.component'
import { ResourceOKR } from '@/components/resource-okr.component'
import { NormalResourceList } from '@/modules/portfolio/portfolio-list'
import { ResourceReport } from '@/modules/project/project-detail/project-summary'
import { PAGE_RESOURCE_POINTS_ENUM, PriorityEnum, ResourceType, ResourceUser } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { Customer } from '../../customer.schema'
import { CustomerEdit, CustomerFormPartType } from '../../customer-edit.component'
import { FlowStatusChart } from '../flow-status-chart/flow-status-chart.component'
import { CustomerReviews } from './customer-reviews.component'

const { Title } = Typography

interface CustomerSummaryContainerProps {
  customer: Customer
  showRightPanel?: boolean
  setUsers?: React.Dispatch<React.SetStateAction<ResourceUser[] | undefined>>
  refresh: () => void
}

export const CustomerSummaryContainer: React.FC<CustomerSummaryContainerProps> = ({
  customer,
  setUsers,
  refresh,
  showRightPanel,
}) => {
  const { t } = useTranslation()
  const customerId = customer.selfId
  const { canUpdate } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况, customer.permissions)

  const [formPartType, setFormPartType] = React.useState<CustomerFormPartType | undefined>()

  const { run: updateCustomer } = useRequest(
    (data) => ({
      url: `/api/customer/patch/${customerId}`,
      method: 'patch',
      data: {
        ...data,
      },
    }),
    {
      manual: true,
      debounceInterval: 500,
      fetchKey: (params) => params.field,
    }
  )

  return (
    <Row gutter={[32, 0]}>
      <Col xs={24} md={showRightPanel ? 16 : 24} className='children:mb-24px'>
        <FlowStatusChart customer={customer} />
        <Collapse defaultActiveKey={['1']} className='production-detail-collapse'>
          <Collapse.Panel
            key={'1'}
            showArrow={false}
            header={
              <div>
                <span className='text-lg'>{t('customer.detail.projectBaseInfo')}</span>
              </div>
            }
            extra={
              <span
                className={`text-xs transition text-blue-500 ${
                  !canUpdate ? 'cursor-not-allowed text-[#00000040]' : ''
                }`}
                onClick={(e) => {
                  if (!canUpdate) return
                  e.stopPropagation()
                  setFormPartType(CustomerFormPartType.BASIC)
                }}
              >
                {t('edit')}
              </span>
            }
          >
            <Descriptions layout='vertical' size='small' className='mt-8px'>
              <Descriptions.Item label={t('customer.timePeriod')}>
                {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, customer.permissions).canRead ? (
                  moment(customer.startDate).utc().format('YYYY-MM-DD') +
                  ' - ' +
                  moment(customer.endDate).utc().format('YYYY-MM-DD')
                ) : (
                  <MosaicComponent />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('customer.detail.level')}>
                {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_服务等级, customer.permissions).canRead ? (
                  customer.level
                ) : (
                  <MosaicComponent />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('customer.detail.priority')}>
                {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, customer.permissions).canRead ? (
                  t(`form.priority.${PriorityEnum[customer.priority]}`)
                ) : (
                  <MosaicComponent />
                )}
              </Descriptions.Item>
              {customer.startDateChangeReason && (
                <Descriptions.Item label={t('customer.detail.changeReason', { field: t('customer.detail.startDate') })}>
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, customer.permissions).canRead ? (
                    <span>{removeHtmlTag(customer.startDateChangeReason || '')}</span>
                  ) : (
                    <MosaicComponent />
                  )}
                </Descriptions.Item>
              )}
              {customer.endDateChangeReason && (
                <Descriptions.Item label={t('customer.detail.changeReason', { field: t('customer.detail.endDate') })}>
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, customer.permissions).canRead ? (
                    <span>{removeHtmlTag(customer.endDateChangeReason || '')}</span>
                  ) : (
                    <MosaicComponent />
                  )}
                </Descriptions.Item>
              )}
              {customer.expectTimeChangeReason && (
                <Descriptions.Item
                  label={t('customer.detail.changeReason', { field: t('customer.detail.expectTime') })}
                >
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, customer.permissions).canRead ? (
                    <span>{removeHtmlTag(customer.expectTimeChangeReason || '')}</span>
                  ) : (
                    <MosaicComponent />
                  )}
                </Descriptions.Item>
              )}
              {customer.priorityChangeReason && (
                <Descriptions.Item label={t('customer.detail.changeReason', { field: t('customer.detail.priority') })}>
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, customer.permissions).canRead ? (
                    <span>{removeHtmlTag(customer.priorityChangeReason || '')}</span>
                  ) : (
                    <MosaicComponent />
                  )}
                </Descriptions.Item>
              )}
              {customer.businessGoalChangeReason && (
                <Descriptions.Item
                  label={t('customer.detail.changeReason', { field: t('customer.detail.businessGoal') })}
                >
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_业务目标, customer.permissions).canRead ? (
                    <span>{removeHtmlTag(customer.businessGoalChangeReason || '')}</span>
                  ) : (
                    <MosaicComponent />
                  )}
                </Descriptions.Item>
              )}
              {customer.goalChangeReason && (
                <Descriptions.Item label={t('customer.detail.changeReason', { field: t('customer.detail.goal') })}>
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_交付目标, customer.permissions).canRead ? (
                    <span>{removeHtmlTag(customer.goalChangeReason || '')}</span>
                  ) : (
                    <MosaicComponent />
                  )}
                </Descriptions.Item>
              )}
              {customer.standardChangeReason && (
                <Descriptions.Item label={t('customer.detail.changeReason', { field: t('customer.detail.standard') })}>
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], customer.permissions)
                    .canRead ? (
                    <span>{removeHtmlTag(customer.standardChangeReason || '')}</span>
                  ) : (
                    <MosaicComponent />
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>
            <Title className='mt-3' level={5}>
              {t('customer.detail.dependencies')}
              <Tooltip title={t('customer.detail.dependenciesDesc')}>
                <QuestionCircleOutlined className='ml-2' />
              </Tooltip>
            </Title>
            <Editor
              editorKey={customer.selfId}
              editable={false}
              canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_依赖项, customer.permissions).canRead}
              placeholder='1）对TO的依赖 2）对商务/服务 的依赖 3）对客户的依赖 4）其它依赖项'
              content={customer.dependencies}
              onSave={(dependencies) => updateCustomer({ dependencies })}
            />
            <br />

            <Title className='mt-3' level={5}>
              {t('customer.detail.background')}
            </Title>
            <Editor
              editorKey={customer.selfId}
              editable={false}
              canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_背景, customer.permissions).canRead}
              content={customer.background}
              onSave={(background) => updateCustomer({ background })}
            />
            <br />
            <Title className='mt-3' level={5}>
              {t('customer.detail.businessGoal')}{' '}
              <Tooltip title={t('customer.detail.businessGoalDesc')}>
                <QuestionCircleOutlined className='ml-2' />
              </Tooltip>
            </Title>
            <Editor
              editorKey={customer.selfId}
              editable={false}
              canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_业务目标, customer.permissions).canRead}
              content={customer.businessGoal}
              onSave={(businessGoal) => updateCustomer({ businessGoal })}
            />
            <br />
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={['1']} className='production-detail-collapse'>
          <Collapse.Panel
            key={'1'}
            showArrow={false}
            header={
              <div>
                <span className='text-lg'>{t('customer.detail.projectDeliveryInfo')}</span>
              </div>
            }
            extra={
              <span
                className={`text-xs transition text-blue-500 ${
                  !canUpdate ? 'cursor-not-allowed text-[#00000040]' : ''
                }`}
                onClick={(e) => {
                  if (!canUpdate) return
                  e.stopPropagation()
                  setFormPartType(CustomerFormPartType.DELIVERY)
                }}
              >
                {t('edit')}
              </span>
            }
          >
            <Title className='mt-3' level={5}>
              {t('customer.detail.goal')}
              <Tooltip title={t('customer.detail.goalDesc')}>
                <QuestionCircleOutlined className='ml-2' />
              </Tooltip>
            </Title>
            <Editor
              editorKey={customer.selfId}
              editable={false}
              canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_交付目标, customer.permissions).canRead}
              content={customer.goal}
              onSave={(goal) => updateCustomer({ goal })}
            />
            <br />
            <Title className='mt-3' level={5}>
              {' '}
              {t('customer.detail.expectTime')}
              <Tooltip title={t('customer.detail.expectTimeDesc')}>
                <QuestionCircleOutlined className='ml-2' />
              </Tooltip>
            </Title>
            {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_预计交付时间, customer.permissions).canRead ? (
              moment(customer.expectTime).format('YYYY-MM-DD')
            ) : (
              <MosaicComponent />
            )}
            <br />
            <Title className='mt-3' level={5}>
              {' '}
              {t('customer.detail.standard')}
              <Tooltip title={t('customer.detail.standardDesc')}>
                <QuestionCircleOutlined className='ml-2' />
              </Tooltip>
            </Title>
            <Editor
              editorKey={customer.selfId}
              editable={false}
              canRead={
                getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], customer.permissions).canRead
              }
              content={customer.standard}
              onSave={(standard) => updateCustomer({ standard })}
            />
            <br />
          </Collapse.Panel>
        </Collapse>

        <ResourceOKR resource={customer} isSummaryPage={true} />
        <ResourceUsers source={customer} setUsers={setUsers} />
        <ResourceReport resource={customer} />
      </Col>

      {showRightPanel && (
        <Col xs={24} md={8}>
          <div>
            <CustomerReviews customer={customer} />
          </div>
        </Col>
      )}

      <Col span={24}>
        <NormalResourceList resource={customer} pageType={ResourceType.CUSTOMER} />
      </Col>
      <CustomerEdit
        customer={customer}
        visible={!!formPartType}
        formPartType={formPartType}
        onCancel={() => {
          setFormPartType(undefined)
          refresh()
        }}
      />
    </Row>
  )
}
